import { useState, useEffect, useCallback } from 'react';
import { assetsService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { writeClientAudit } from '../lib/clientAudit';
import { assetAuditSummary } from '../lib/moduleAudit';
import { initialAssets } from '../data/assets';
import type { RestaurantAsset } from '../types';

interface UseAssetsReturn {
  assets: RestaurantAsset[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (item: Omit<RestaurantAsset, 'id'> & { id?: string }) => Promise<RestaurantAsset>;
  update: (id: string, updates: Partial<RestaurantAsset>) => Promise<RestaurantAsset>;
  remove: (id: string) => Promise<void>;
}

export const useAssets = (enabled = true): UseAssetsReturn => {
  const [assets, setAssets] = useState<RestaurantAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssets = useCallback(async () => {
    const dataSource = getDataSource();

    if (dataSource === 'mock') {
      setAssets(initialAssets);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await assetsService.getAll();
      setAssets(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load assets';
      setError(errorMessage);
      console.error('Error fetching assets:', err);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchAssets();
  }, [fetchAssets, enabled]);

  const create = useCallback(async (item: Omit<RestaurantAsset, 'id'> & { id?: string }): Promise<RestaurantAsset> => {
    const dataSource = getDataSource();

    if (dataSource === 'mock') {
      const newItem: RestaurantAsset = {
        ...item,
        id: item.id ?? `AST-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      };
      setAssets((prev) => [...prev, newItem]);
      return newItem;
    }

    setError(null);
    try {
      const newItem = await assetsService.create(item);
      setAssets((prev) => [...prev, newItem]);
      writeClientAudit({
        action: 'asset.created',
        entityType: 'asset',
        entityId: newItem.id,
        summary: assetAuditSummary('added', newItem.name, newItem.quantity),
      });
      return newItem;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create asset';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const update = useCallback(async (id: string, updates: Partial<RestaurantAsset>): Promise<RestaurantAsset> => {
    const dataSource = getDataSource();

    if (dataSource === 'mock') {
      let next: RestaurantAsset | undefined;
      setAssets((prev) =>
        prev.map((a) => {
          if (a.id !== id) return a;
          next = { ...a, ...updates };
          return next;
        }),
      );
      if (!next) throw new Error('Asset not found');
      return next;
    }

    setError(null);
    try {
      const previous = assets.find((asset) => asset.id === id);
      const updatedItem = await assetsService.update(id, updates);
      setAssets((prev) => prev.map((a) => (a.id === id ? updatedItem : a)));
      writeClientAudit({
        action: 'asset.updated',
        entityType: 'asset',
        entityId: id,
        summary: assetAuditSummary('saved', updatedItem.name || previous?.name || 'Asset', updatedItem.quantity, previous as unknown as Record<string, unknown>, updates as Record<string, unknown>),
      });
      return updatedItem;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update asset';
      setError(errorMessage);
      throw err;
    }
  }, [assets]);

  const remove = useCallback(async (id: string): Promise<void> => {
    const dataSource = getDataSource();

    if (dataSource === 'mock') {
      setAssets((prev) => prev.filter((a) => a.id !== id));
      return;
    }

    setError(null);
    try {
      const previous = assets.find((asset) => asset.id === id);
      await assetsService.delete(id);
      setAssets((prev) => prev.filter((a) => a.id !== id));
      writeClientAudit({
        action: 'asset.removed',
        entityType: 'asset',
        entityId: id,
        summary: assetAuditSummary('removed', previous?.name || 'Asset'),
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete asset';
      setError(errorMessage);
      throw err;
    }
  }, [assets]);

  return {
    assets,
    loading,
    error,
    refresh: fetchAssets,
    create,
    update,
    remove,
  };
};
